import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Badge, Spinner } from 'react-bootstrap';
import EventoService from '../../services/eventoService';
import { getTramites } from '../../services/tramiteService';
import { getTipoTramites } from '../../services/tipoTramiteService';
import PageHeader from '../../components/ui/PageHeader';
import '../../style/pages/HomePage.css';

/**
 * Página de inicio del área ART: bienvenida, próximos eventos (de trámites ART) y listado de trámites ART.
 */
const ARTInicio = () => {
    const navigate = useNavigate();
    const [idTipoART, setIdTipoART] = useState(null);
    const [loadingId, setLoadingId] = useState(true);
    const [proximosEventos, setProximosEventos] = useState([]);
    const [loadingEventos, setLoadingEventos] = useState(true);

    // Id del tipo ART en BD (V2/V34). Fallback por si la API no devuelve el tipo.
    const ID_TIPO_ART_BD = 6;

    // Obtener id del tipo ART
    useEffect(() => {
        const cargar = async () => {
            try {
                const tipos = await getTipoTramites();
                const art = (tipos || []).find(
                    (t) => (t.nombre || '').toUpperCase().includes('ART') || t.idTipoTramite === ID_TIPO_ART_BD
                );
                if (art && art.idTipoTramite != null) {
                    setIdTipoART(art.idTipoTramite);
                } else {
                    setIdTipoART(ID_TIPO_ART_BD);
                }
            } catch (e) {
                console.error('Error al cargar tipo ART:', e);
                setIdTipoART(ID_TIPO_ART_BD);
            } finally {
                setLoadingId(false);
            }
        };
        cargar();
    }, []);

    // Eventos próximos solo de trámites ART
    useEffect(() => {
        if (idTipoART == null) return;
        const cargarEventos = async () => {
            setLoadingEventos(true);
            try {
                const [eventosRes, tramitesART] = await Promise.all([
                    EventoService.getEventos(),
                    getTramites(null, idTipoART, null)
                ]);
                const eventos = eventosRes.data || [];
                const idsART = new Set((tramitesART || []).map((t) => t.idTramite).filter(Boolean));

                const hoy = new Date();
                hoy.setHours(0, 0, 0, 0);

                const eventosFuturos = eventos.filter((e) => {
                    const fechaStr = e.fecha?.includes('T') ? e.fecha : `${e.fecha || ''}T00:00:00`;
                    const fechaEvento = new Date(fechaStr);
                    const esDeART = !e.idTramite || idsART.has(e.idTramite);
                    return fechaEvento >= hoy && esDeART;
                });

                eventosFuturos.sort((a, b) => {
                    const fechaA = new Date(`${a.fecha}T${a.horaInicio || '00:00'}`);
                    const fechaB = new Date(`${b.fecha}T${b.horaInicio || '00:00'}`);
                    return fechaA - fechaB;
                });

                setProximosEventos(eventosFuturos.slice(0, 4));
            } catch (error) {
                console.error('Error al cargar eventos ART:', error);
            } finally {
                setLoadingEventos(false);
            }
        };
        cargarEventos();
    }, [idTipoART]);

    const formatearFecha = (fechaStr) => {
        if (!fechaStr) return '';
        const part = (fechaStr.split('T')[0] || '').split('-');
        if (part.length < 3) return fechaStr;
        return `${part[2]}/${part[1]}/${part[0]}`;
    };

    const handleNavegacionEvento = (evento) => {
        if (evento.idTramite) {
            navigate(`/art/tramites/${evento.idTramite}`);
        } else {
            navigate('/calendario');
        }
    };

    if (loadingId) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '40vh' }}>
                <Spinner animation="border" variant="primary" />
                <span className="ms-3">Cargando área ART...</span>
            </div>
        );
    }

    return (
        <div className="homepage-container area-art">
            <PageHeader
                variante="plano"
                icono="heart-pulse-fill"
                titulo="Área ART"
                descripcion="Accidentes de Trabajo y Riesgos. Gestión de trámites y eventos ART."
            />

            {/* Próximos eventos (solo de trámites ART) */}
            <div className="eventos-section" style={{ marginBottom: '2rem' }}>
                <div className="eventos-header-row">
                    <h2 className="eventos-section-title">
                        <i className="bi bi-calendar-event me-2"></i>
                        Próximos eventos ART
                    </h2>
                    <button
                        type="button"
                        className="btn-ver-calendario-home"
                        onClick={() => navigate('/calendario')}
                    >
                        Ver calendario
                        <i className="bi bi-arrow-right ms-2"></i>
                    </button>
                </div>

                {loadingEventos ? (
                    <div className="loading-state">
                        <Spinner animation="border" variant="primary" size="sm" />
                        <p className="mt-2 mb-0 small">Cargando eventos...</p>
                    </div>
                ) : proximosEventos.length === 0 ? (
                    <div className="empty-state-eventos compact">
                        <i className="bi bi-calendar-check"></i>
                        <p className="mb-0">No hay eventos próximos para trámites ART.</p>
                    </div>
                ) : (
                    <Row xs={1} md={2} lg={4} className="g-3">
                        {proximosEventos.map((evento) => (
                            <Col key={evento.idEvento}>
                                <div
                                    className="evento-card-home"
                                    onClick={() => handleNavegacionEvento(evento)}
                                >
                                    <div className="evento-badges">
                                        <Badge bg="warning" text="dark" className="badge-tipo-home">
                                            {evento.tipoEvento || 'Evento'}
                                        </Badge>
                                        {evento.idTramite && (
                                            <Badge className="badge-tramite-home">Trámite #{evento.idTramite}</Badge>
                                        )}
                                    </div>
                                    <h6 className="evento-titulo-home" title={evento.titulo}>{evento.titulo}</h6>
                                    <div className="evento-info-home">
                                        <div className="info-item-home">
                                            <i className="bi bi-calendar3 me-2"></i>
                                            {formatearFecha(evento.fecha)}
                                        </div>
                                        <div className="info-item-home">
                                            <i className="bi bi-clock me-2"></i>
                                            {evento.todoElDia ? 'Todo el día' : `${evento.horaInicio?.substring(0, 5)} hs`}
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                )}
            </div>

            {/* Accesos rápidos ART */}
            <div className="accesos-rapidos-home">
                <div className="acceso-card acceso-card-art" onClick={() => navigate('/art/tramites')}>
                    <i className="bi bi-folder2-open acceso-card-icon"></i>
                    <div className="acceso-card-body">
                        <h5>Trámites ART</h5>
                        <p>Ver, editar e iniciar trámites de accidentes de trabajo</p>
                    </div>
                    <i className="bi bi-arrow-right-short acceso-card-arrow"></i>
                </div>
                <div className="acceso-card acceso-card-clientes" onClick={() => navigate('/clientes')}>
                    <i className="bi bi-people-fill acceso-card-icon"></i>
                    <div className="acceso-card-body">
                        <h5>Clientes</h5>
                        <p>Consultar y administrar el listado de clientes</p>
                    </div>
                    <i className="bi bi-arrow-right-short acceso-card-arrow"></i>
                </div>
                <div className="acceso-card acceso-card-agenda" onClick={() => navigate('/calendario')}>
                    <i className="bi bi-calendar-event-fill acceso-card-icon"></i>
                    <div className="acceso-card-body">
                        <h5>Agenda</h5>
                        <p>Ver todos los eventos y vencimientos programados</p>
                    </div>
                    <i className="bi bi-arrow-right-short acceso-card-arrow"></i>
                </div>
            </div>
        </div>
    );
};

export default ARTInicio;

